import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Replayer, EventType } from 'rrweb';
import 'rrweb/dist/style.css';
import { Loader2, ScreenShare, MousePointer2 } from 'lucide-react';
import { useCollab, colorForUser, initialsFor } from '@/contexts/CollabContext';

// Guest-side live mirror: reconstructs the host's DOM from streamed rrweb
// events, scales it to fit, overlays live cursors, and (when this guest holds
// control) forwards clicks / keystrokes to the host.
export default function MirrorView() {
  const { session, isController, amController, subscribeMirror, requestMirror, sendInput, cursors, currentUserId, isHost } = useCollab();
  const rootRef = useRef(null);
  const replayerRef = useRef(null);
  const bufferRef = useRef([]);
  const startedRef = useRef(false);
  const metaRef = useRef({ width: 0, height: 0 });
  const [ready, setReady] = useState(false);
  const [rect, setRect] = useState({ left: 0, top: 0, w: 0, h: 0, scale: 1 });

  const show = !!session && !isHost; // only guests view the mirror (host is the streamer)

  useEffect(() => {
    if (!show) return;
    startedRef.current = false;
    bufferRef.current = [];
    setReady(false);

    const applyScale = () => {
      const wrap = replayerRef.current?.wrapper;
      const root = rootRef.current;
      const { width, height } = metaRef.current;
      if (!wrap || !root || !width || !height) return;
      const cw = root.clientWidth, ch = root.clientHeight;
      const scale = Math.min(cw / width, ch / height);
      const left = Math.max(0, (cw - width * scale) / 2);
      const top = Math.max(0, (ch - height * scale) / 2);
      wrap.style.position = 'absolute';
      wrap.style.transformOrigin = 'top left';
      wrap.style.transform = `scale(${scale})`;
      wrap.style.left = `${left}px`;
      wrap.style.top = `${top}px`;
      setRect({ left, top, w: width * scale, h: height * scale, scale });
    };

    const onEvent = (event) => {
      if (!event || typeof event.type !== 'number') return;
      if (!startedRef.current) {
        bufferRef.current.push(event);
        const meta = bufferRef.current.find((e) => e.type === EventType.Meta);
        const hasFull = bufferRef.current.some((e) => e.type === EventType.FullSnapshot);
        if (!hasFull) return;
        if (meta?.data) metaRef.current = { width: meta.data.width, height: meta.data.height };
        try {
          const replayer = new Replayer(bufferRef.current.slice(), {
            root: rootRef.current, liveMode: true, mouseTail: false,
            UNSAFE_replayCanvas: true, showWarning: false,
          });
          replayer.startLive(bufferRef.current[0].timestamp);
          replayerRef.current = replayer;
          startedRef.current = true;
          bufferRef.current = [];
          setReady(true);
          setTimeout(applyScale, 50);
        } catch { /* wait for next snapshot */ }
        return;
      }
      if (event.type === EventType.Meta && event.data) {
        metaRef.current = { width: event.data.width, height: event.data.height };
        setTimeout(applyScale, 30);
      }
      try { replayerRef.current.addEvent(event); } catch { /* ignore */ }
    };

    const off = subscribeMirror(onEvent);
    requestMirror();
    const retry = setInterval(() => { if (!startedRef.current) requestMirror(); }, 1500);
    window.addEventListener('resize', applyScale);

    return () => {
      off(); clearInterval(retry);
      window.removeEventListener('resize', applyScale);
      try { replayerRef.current?.pause(); } catch { /* ignore */ }
      replayerRef.current = null;
      const root = rootRef.current;
      if (root) root.innerHTML = '';
    };
  }, [show, session?.sessionId, subscribeMirror, requestMirror]);

  // Controlling guest: forward keystrokes to the host.
  useEffect(() => {
    if (!show || !amController) return;
    const onKey = (e) => {
      const k = e.key;
      if (k.length === 1) sendInput({ kind: 'value', value: (document.activeElement?.value || '') });
      sendInput({ kind: 'key', key: k });
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [show, amController, sendInput]);

  if (!show) return null;

  // Map a click on the mirror back to host-viewport coordinates.
  const onCaptureClick = (e) => {
    if (!amController || !rect.scale) return;
    const x = (e.clientX - rect.left) / rect.scale;
    const y = (e.clientY - rect.top) / rect.scale;
    if (x < 0 || y < 0) return;
    sendInput({ kind: 'click', x, y });
  };

  return createPortal(
    <div className="fixed inset-0 z-[9990] bg-[#0a0a0b]" data-collab-norecord data-testid="collab-mirror-view">
      <style>{`.rr-block, .replayer-mouse, .replayer-mouse-tail { display: none !important; }`}</style>
      <div className="absolute top-3 left-1/2 -translate-x-1/2 z-[3] flex items-center gap-1.5 px-3 py-1 rounded-full bg-black/70 border border-white/10 text-[11px] text-white/90" data-collab-norecord>
        {amController
          ? <><MousePointer2 className="w-3.5 h-3.5 text-emerald-400" /> You have control — clicks & typing act on the host's screen</>
          : <><ScreenShare className="w-3.5 h-3.5 text-emerald-400" /> Viewing the host's screen (view-only)</>}
      </div>

      {!ready && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-white/70 z-[1]">
          <Loader2 className="w-6 h-6 animate-spin" />
          <p className="text-xs">Connecting to the host's screen…</p>
        </div>
      )}

      <div ref={rootRef} className="absolute inset-0 overflow-hidden" />

      {/* Live cursors on the mirror (host + other guests) */}
      <div className="absolute inset-0 pointer-events-none z-[2]" data-collab-norecord aria-hidden>
        {Object.entries(cursors).filter(([uid]) => uid !== currentUserId).map(([uid, c]) => (
          <div key={uid} className="absolute" style={{ left: rect.left + c.x * rect.w, top: rect.top + c.y * rect.h, transition: 'left 60ms linear, top 60ms linear' }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M5 3l14 7-6 2-2 6-6-15z" fill={colorForUser(uid)} stroke="white" strokeWidth="1.2" strokeLinejoin="round" /></svg>
            <div className="mt-0.5 px-1 py-0.5 rounded text-[9px] font-semibold text-white whitespace-nowrap" style={{ background: colorForUser(uid) }}>{c.name || 'Guest'}</div>
          </div>
        ))}
      </div>

      {/* Input capture layer — only active when this guest holds control */}
      {amController && (
        <div className="absolute z-[4]" style={{ left: rect.left, top: rect.top, width: rect.w, height: rect.h, cursor: 'pointer' }} onClick={onCaptureClick} data-testid="collab-control-capture" data-collab-norecord />
      )}
    </div>,
    document.body,
  );
}
